import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { User, LogOut, BookmarkIcon, Settings, ChevronDown, Search, MessageSquare } from 'lucide-react';

export default function Header() {
  const { user, userProfile, signOut } = useAuth();
  const navigate = useNavigate();
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const dropdownRef = useRef(null);
  const searchRef = useRef(null);

  useEffect(() => {
    // Close dropdown when clicking outside
    const handleClickOutside = (event) => {
      if (dropdownRef?.current && !dropdownRef?.current?.contains(event?.target)) {
        setIsProfileDropdownOpen(false);
      }
      if (searchRef?.current && !searchRef?.current?.contains(event?.target)) {
        setIsSearchFocused(false);
      }
    };

    // Close dropdown on escape key
    const handleEscape = (event) => {
      if (event?.key === 'Escape') {
        setIsProfileDropdownOpen(false);
        setIsSearchFocused(false);
      }
    };

    document?.addEventListener('mousedown', handleClickOutside);
    document?.addEventListener('keydown', handleEscape);

    return () => {
      document?.removeEventListener('mousedown', handleClickOutside);
      document?.removeEventListener('keydown', handleEscape);
    };
  }, []);

  const handleSignOut = async () => {
    await signOut();
    setIsProfileDropdownOpen(false);
    navigate('/');
  };

  const handleProfileClick = () => {
    setIsProfileDropdownOpen(!isProfileDropdownOpen);
  };

  const handleSearch = (e) => {
    e?.preventDefault();
    if (searchQuery?.trim()) {
      navigate(`/wiki-page?search=${encodeURIComponent(searchQuery)}`);
      setSearchQuery('');
      setIsSearchFocused(false);
    }
  };

  const handleB2BAssistantClick = () => {
    navigate('/wiki-page?openChat=true');
  };

  return (
    <header className="bg-white shadow-md fixed top-0 left-0 right-0 z-50">
      <nav className="container mx-auto px-4 py-4">
        <div className="flex items-center justify-between gap-4">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 flex-shrink-0">
            <img 
              src="/assets/images/b_two_b_codes_logo_new.png" 
              alt="B2B Codes - Professional B2B Data Intelligence Platform"
              className="h-10 w-auto"
            />
            <span className="text-xl font-bold text-gray-900">
              B2B Codes
            </span>
          </Link>

          {/* Search Bar */}
          <div 
            ref={searchRef}
            className={`hidden md:flex items-center flex-1 max-w-md mx-4 transition-all ${
              isSearchFocused ? 'ring-2 ring-blue-500' : ''
            }`}
          >
            <form onSubmit={handleSearch} className="w-full">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e?.target?.value)}
                  onFocus={() => setIsSearchFocused(true)}
                  placeholder="Search knowledge base..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </form>
          </div>

          {/* Navigation Links */}
          <div className="hidden md:flex items-center space-x-6">
            <Link to="/" className="text-gray-700 hover:text-blue-600">Home</Link>
            <Link to="/solutions-categories" className="text-gray-700 hover:text-blue-600">Solutions</Link>
            <Link to="/resource-library" className="text-gray-700 hover:text-blue-600">Resources</Link>
            <Link to="/questions" className="text-gray-700 hover:text-blue-600">Q & A</Link>
            <Link to="/authority-center" className="text-gray-700 hover:text-blue-600">Authority</Link>
            <Link to="/wiki-page" className="text-gray-700 hover:text-blue-600">Wiki</Link>
          </div>

          {/* B2B Assistant Button */}
          <button
            onClick={handleB2BAssistantClick}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
            title="Open B2B Assistant"
          >
            <MessageSquare className="w-5 h-5" />
            <span className="hidden lg:inline">B2B Assistant</span>
          </button>

          {/* Authentication Section */}
          {user ? (
            <div className="flex items-center gap-4 ml-4 pl-4 border-l border-gray-200">
              <Link
                to="/user-dashboard"
                className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
              >
                <BookmarkIcon className="w-5 h-5" />
                <span className="hidden lg:inline">Dashboard</span>
              </Link>
              
              {/* Profile Dropdown */}
              <div className="relative" ref={dropdownRef}>
                <button
                  onClick={handleProfileClick}
                  className="flex items-center gap-2 px-3 py-2 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                  aria-haspopup="true"
                  aria-expanded={isProfileDropdownOpen}
                >
                  <User className="w-5 h-5 text-gray-600" />
                  <span className="text-sm font-medium text-gray-700 hidden lg:inline">
                    {userProfile?.full_name || user?.email?.split('@')?.[0]}
                  </span>
                  <ChevronDown className={`w-4 h-4 text-gray-600 transition-transform ${
                    isProfileDropdownOpen ? 'rotate-180' : ''
                  }`} />
                </button>

                {/* Dropdown Menu */}
                {isProfileDropdownOpen && (
                  <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
                    <div className="px-4 py-2 border-b border-gray-100">
                      <p className="text-sm font-semibold text-gray-900 truncate">
                        {userProfile?.full_name || user?.email?.split('@')?.[0]}
                      </p>
                      <p className="text-xs text-gray-500 truncate">{user?.email}</p>
                    </div>

                    <button
                      onClick={() => {
                        navigate('/user-dashboard?settings=true');
                        setIsProfileDropdownOpen(false);
                      }}
                      className="w-full flex items-center gap-3 px-4 py-2 text-gray-700 hover:bg-gray-50 transition-colors text-left"
                    >
                      <Settings className="w-5 h-5" />
                      <span>Profile Settings</span>
                    </button>

                    <div className="my-1 border-t border-gray-200" />

                    <button
                      onClick={handleSignOut}
                      className="w-full flex items-center gap-3 px-4 py-2 text-red-600 hover:bg-red-50 transition-colors text-left"
                    >
                      <LogOut className="w-5 h-5" />
                      <span>Sign Out</span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-3 ml-4 pl-4 border-l border-gray-200">
              <button
                onClick={() => navigate('/sign-in')}
                className="px-4 py-2 text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors font-medium"
              >
                Login
              </button>
              <button
                onClick={() => navigate('/sign-up')}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
              >
                Register
              </button>
            </div>
          )}
        </div>
      </nav>
    </header>
  );
}